import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ValidationState,
  DataProfile,
  ProfilingError,
} from "datasets/pages/tables/components/file-validation/types";
import {
  parseValidationJobResults,
  parseProfilingJobResults,
  recoverSourceFileUri,
} from "datasets/pages/tables/components/file-validation/utils/jobParsers";
import {
  JobResponse,
  DataIngestionResponse,
  TaskResult,
} from "datasets/pages/tables/types/interfaces/ingestion-job";
import { useFetcher } from "~/hooks/useFetcher";
import { useStartProfiling } from "./useStartProfiling";
import { useStartValidation } from "./useStartValidation";

const { VITE_CATALOG_API } = import.meta.env;

const JOB_POLL_INTERVAL_MS = 3_000;

// Terminal/completed statuses
const COMPLETED_STATUSES = ["PASSED", "WARNING", "FAILED"] as const;
type CompletedStatus = (typeof COMPLETED_STATUSES)[number];

// Type guard function
const isValidationJobComplete = (status?: string): status is CompletedStatus => {
  return status !== undefined && COMPLETED_STATUSES.includes(status as any);
};

interface UseValidationAndProfilingOptions {
  /** The S3 URI of the file to validate/profile. Jobs only start when this is provided. */
  sourceFile?: string | null;
}

export const useValidationAndProfiling = (options: UseValidationAndProfilingOptions = {}) => {
  const { sourceFile } = options;
  const { datasetId: dataset_id } = useParams();
  const [searchParams] = useSearchParams();
  const data_ingestion_id = searchParams.get("ingestion");
  const [validationJobId, setValidationJobId] = useState<string | null>(null);
  const [profilingJobId, setProfilingJobId] = useState<string | null>(null);
  const [hasStartedValidation, setHasStartedValidation] = useState(false);
  const [hasStartedProfiling, setHasStartedProfiling] = useState(false);
  const [validationState, setValidationState] = useState<ValidationState>({
    jobs: [],
    summary: {
      totalRows: 0,
      headerRow: false,
      dataRows: 0,
      errorRows: 0,
      cleanRows: 0,
    },
    isComplete: false,
    hasErrors: false,
    hasWarnings: false,
  });

  const [dataProfile, setDataProfile] = useState<DataProfile | null>(null);
  const [profilingError, setProfilingError] = useState<ProfilingError | null>(null);

  const [isValidating, setIsValidating] = useState(false);
  const [isProfiling, setIsProfiling] = useState(false);

  const { data: ingestion, isLoading: isLoadingIngestion } = useFetcher<DataIngestionResponse>(
    dataset_id && data_ingestion_id
      ? `/api/catalog/datasets/${dataset_id}/ingestions/${data_ingestion_id}`
      : null
  );

  // Fetch existing jobs when we have an ingestion but no sourceFile (page refresh scenario)
  // This allows us to display results from previously started jobs
  const { data: existingJobs } = useFetcher<JobResponse[]>(
    dataset_id && data_ingestion_id && !sourceFile
      ? `${VITE_CATALOG_API}/datasets/${dataset_id}/ingestions/${data_ingestion_id}/jobs`
      : null
  );

  // Source file recovered from job results (page refresh scenario)
  const [recoveredSourceFile, setRecoveredSourceFile] = useState<string | null>(null);

  // Track ingestion ID changes to reset state when user starts new ingestion
  const prevIngestionIdRef = useRef<string | null>(null);

  useEffect(() => {
    const prevId = prevIngestionIdRef.current;
    // Reset when transitioning between ingestions (not on initial load)
    if (prevId && data_ingestion_id && prevId !== data_ingestion_id) {
      setValidationJobId(null);
      setProfilingJobId(null);
      setHasStartedValidation(false);
      setHasStartedProfiling(false);
      setIsValidating(false);
      setIsProfiling(false);
      setRecoveredSourceFile(null);
      setValidationState({
        jobs: [],
        summary: {
          totalRows: 0,
          headerRow: false,
          dataRows: 0,
          errorRows: 0,
          cleanRows: 0,
        },
        isComplete: false,
        hasErrors: false,
        hasWarnings: false,
      });
      setDataProfile(null);
      setProfilingError(null);
    }
    prevIngestionIdRef.current = data_ingestion_id;
  }, [data_ingestion_id]);

  // Resume polling for existing jobs on page refresh
  useEffect(() => {
    if (existingJobs && existingJobs.length > 0 && !sourceFile) {
      const validationJob = existingJobs.find((job) => job.job_type === "validation");
      const profilingJob = existingJobs.find((job) => job.job_type === "profiling");

      // Recover the original source file URI from job results
      if (!recoveredSourceFile) {
        const fileUri = recoverSourceFileUri(existingJobs);
        if (fileUri) {
          setRecoveredSourceFile(fileUri);
        }
      }

      if (validationJob && !validationJobId) {
        setValidationJobId(validationJob.job_id);
        setHasStartedValidation(true);
        // Check if already complete
        if (!isValidationJobComplete(validationJob.status)) {
          setIsValidating(true);
        }
      }

      if (profilingJob && !profilingJobId) {
        setProfilingJobId(profilingJob.job_id);
        setHasStartedProfiling(true);
        // Check if already complete
        if (!isValidationJobComplete(profilingJob.status)) {
          setIsProfiling(true);
        }
      }
    }
  }, [existingJobs, sourceFile, validationJobId, profilingJobId, recoveredSourceFile]);

  const { data: validationJobData, error: validationJobError } = useFetcher<JobResponse>(
    validationJobId
      ? `${VITE_CATALOG_API}/datasets/${dataset_id}/ingestions/${data_ingestion_id}/jobs/${validationJobId}`
      : null,
    {
      refreshInterval: (latestData?: JobResponse, error?: { status?: number }) => {
        // Stop polling if 404 or other error
        // TODO SCDM-2448 should we stop polling for 500s or try again?
        if (error?.status === 404) {
          return 0;
        }

        // Continue polling until we have all three validation tasks
        if (latestData?.results?.data) {
          try {
            const parsedResults = JSON.parse(latestData.results.data);

            // Stop polling only when all three validation tasks are present
            if (parsedResults.tasks) {
              const hasFileTypeValidation = parsedResults.tasks.some(
                (t: TaskResult) => t.message.task_name === "FileTypeValidationTaskMessage"
              );
              const hasHeaderValidation = parsedResults.tasks.some(
                (t: TaskResult) => t.message.task_name === "FileHeaderValidationTaskMessage"
              );
              const hasRowValidation = parsedResults.tasks.some(
                (t: TaskResult) => t.message.task_name === "FileRowValidationTaskMessage"
              );

              if (hasFileTypeValidation && hasHeaderValidation && hasRowValidation) {
                return 0;
              }
            }
          } catch (e) {
            // Continue polling if parse fails
          }
        }

        // Poll every 3 seconds while waiting for results
        return JOB_POLL_INTERVAL_MS;
      },
    }
  );

  const { data: profilingJobData, error: profilingJobError } = useFetcher<JobResponse>(
    profilingJobId
      ? `${VITE_CATALOG_API}/datasets/${dataset_id}/ingestions/${data_ingestion_id}/jobs/${profilingJobId}`
      : null,
    {
      refreshInterval: (latestData?: JobResponse, error?: { status?: number }) => {
        // Stop polling if 404 or other error
        // TODO SCDM-2448 should we stop polling for 500s or try again?
        if (error?.status === 404) {
          return 0;
        }

        // Continue polling until complete
        if (latestData?.results?.data) {
          try {
            const parsedResults = JSON.parse(latestData.results.data);

            // Stop polling when message complete
            if (parsedResults.message) {
              return 0;
            }
          } catch (e) {
            // Continue polling if parse fails
          }
        }

        // Poll every 3 seconds while waiting for results
        return JOB_POLL_INTERVAL_MS;
      },
    }
  );

  // Handle 404 error for missing job
  useEffect(() => {
    if (validationJobError?.status === 404) {
      console.error("Job not found (404):", validationJobId);
      setValidationState({
        jobs: [
          {
            title: "Job Not Found",
            description: "The validation job could not be found",
            result: "Job ID may be invalid or expired",
            status: "failed",
          },
        ],
        summary: {
          totalRows: 0,
          headerRow: false,
          dataRows: 0,
          errorRows: 0,
          cleanRows: 0,
        },
        isComplete: true,
        hasErrors: true,
        hasWarnings: false,
      });
      setIsValidating(false);
    }
  }, [validationJobError, validationJobId]);

  // Handle 404 error for missing profiling job
  useEffect(() => {
    if (profilingJobError?.status === 404) {
      console.error("Profiling job not found (404):", profilingJobId);
      setDataProfile(null);
      setProfilingError({
        message: "Profiling job could not be found. It may have expired or been deleted.",
        timestamp: new Date().toISOString(),
        retryable: false,
      });
      setIsProfiling(false);
    }
  }, [profilingJobError, profilingJobId]);

  // Update validation state based on job data
  useEffect(() => {
    if (validationJobData) {
      const isComplete = isValidationJobComplete(validationJobData.status);

      // Use shared parser for core field extraction
      const parsed = parseValidationJobResults(validationJobData);

      // Job failed with error message but no tasks
      if (parsed.hasErrors && !parsed.tasks) {
        setValidationState({
          jobs: [
            {
              title: "Validation Job Failed",
              description: "The validation job encountered an error",
              result: "Validation job failed",
              status: "failed",
            },
          ],
          summary: {
            totalRows: 0,
            headerRow: false,
            dataRows: 0,
            errorRows: 0,
            cleanRows: 0,
          },
          isComplete: true,
          hasErrors: true,
          hasWarnings: false,
        });
        setIsValidating(false);
        return;
      }

      // No tasks yet — still waiting for results
      if (!parsed.tasks) {
        if (isComplete) {
          setIsValidating(false);
        }
        return;
      }

      const tasks = parsed.tasks;

      // Map raw tasks to UI-specific ValidationJob cards
      const jobs = tasks.map((task: TaskResult) => {
        const taskName = task.message.task_name;
        const state = task.state; // PASSED, FAILED, WARNING, etc.

        // Determine status based on TaskState
        let status: "passed" | "warning" | "failed" | "running";
        if (state === "FAILED") {
          status = "failed";
        } else if (state === "WARNING") {
          status = "warning";
        } else if (!isValidationJobComplete(state)) {
          status = "running";
        } else {
          status = "passed";
        }

        // Map task types to job titles
        switch (taskName) {
          case "FileTypeValidationTaskMessage":
            return {
              title: "File Format Detection",
              description: "Confirming that this is a tabular file",
              result: task.message.is_valid
                ? `File type: ${task.message.file_type}`
                : "Invalid file type",
              status,
            };

          case "FileHeaderValidationTaskMessage": {
            const getHeaderMessage = () => {
              if (task.message.has_header) {
                return "Header detected";
              }
              switch (task.message.header_state) {
                case "TOO_FEW_ROWS":
                  return "File has too few rows to validate header";
                case "NO_HEADER":
                  return "No header row detected";
                default:
                  return `No header: ${task.message.header_state}`;
              }
            };

            // Build warnings from invalid columns
            const warnings: string[] = [];
            if (
              Array.isArray(task.message.invalid_columns) &&
              task.message.invalid_columns.length > 0
            ) {
              warnings.push(`Invalid column names: ${task.message.invalid_columns.join(", ")}`);
            }

            return {
              title: "Header Detection",
              description: "Detecting if the file has a header row",
              result: getHeaderMessage(),
              status,
              details:
                Array.isArray(task.message.valid_columns) && task.message.valid_columns.length > 0
                  ? `Column names: ${task.message.valid_columns.join(", ")}`
                  : undefined,
              warnings: warnings.length > 0 ? warnings : undefined,
            };
          }

          case "FileRowValidationTaskMessage": {
            const totalRows =
              (task.message.valid_row_count ?? 0) + (task.message.invalid_row_count ?? 0);
            const invalidRowCount = task.message.invalid_row_count ?? 0;

            const getRowMessage = () => {
              if (totalRows === 0) {
                return "No data rows found in file";
              }
              return `${task.message.valid_row_count} valid rows, ${invalidRowCount} invalid rows`;
            };

            // Build warnings for invalid rows
            const warnings: string[] = [];
            if (invalidRowCount > 0) {
              warnings.push(
                `${invalidRowCount} row${
                  invalidRowCount > 1 ? "s" : ""
                } could not be parsed correctly`
              );
            }

            return {
              title: "File Structure",
              description: "Analyzing file structure and detecting parsing errors",
              result: getRowMessage(),
              status,
              details: totalRows > 0 ? `Total rows: ${totalRows}` : undefined,
              warnings: warnings.length > 0 ? warnings : undefined,
            };
          }

          default:
            return {
              title: taskName,
              description: "Unknown task",
              result: `State: ${state}`,
              status,
            };
        }
      });

      // Build summary from specific task types
      const headerTask = tasks.find(
        (t: TaskResult) => t.message.task_name === "FileHeaderValidationTaskMessage"
      );
      const rowValidationTask = tasks.find(
        (t: TaskResult) => t.message.task_name === "FileRowValidationTaskMessage"
      );

      const summary = {
        totalRows: rowValidationTask
          ? (rowValidationTask.message.valid_row_count ?? 0) +
            (rowValidationTask.message.invalid_row_count ?? 0)
          : 0,
        headerRow:
          headerTask && typeof headerTask.message.has_header === "boolean"
            ? headerTask.message.has_header
            : false,
        dataRows: rowValidationTask ? rowValidationTask.message.valid_row_count ?? 0 : 0,
        errorRows: rowValidationTask ? rowValidationTask.message.invalid_row_count ?? 0 : 0,
        cleanRows: rowValidationTask ? rowValidationTask.message.valid_row_count ?? 0 : 0,
      };

      // Only mark as complete if job is complete AND we have all three validation tasks
      const fileTypeTask = tasks.find(
        (t: TaskResult) => t.message.task_name === "FileTypeValidationTaskMessage"
      );
      const hasAllThreeTasks =
        fileTypeTask !== undefined && headerTask !== undefined && rowValidationTask !== undefined;
      const shouldMarkComplete = isComplete && hasAllThreeTasks;

      setValidationState({
        jobs,
        summary,
        isComplete: shouldMarkComplete,
        validatedFilePath: parsed.validatedFilePath ?? undefined,
        hasErrors: parsed.hasErrors,
        hasWarnings: parsed.hasWarnings,
      });

      if (isComplete) {
        setIsValidating(false);
      }
    }
  }, [validationJobData]);

  // Update profiling state based on job data
  useEffect(() => {
    if (profilingJobData) {
      const isComplete = isValidationJobComplete(profilingJobData.status);

      // Use shared parser for core extraction
      const profile = parseProfilingJobResults(profilingJobData, validationState.summary.errorRows);

      if (profile) {
        // Success — shared parser produced a DataProfile
        setDataProfile(profile);
        setProfilingError(null);
        setIsProfiling(false);
      } else if (profilingJobData.results?.data) {
        // Shared parser returned null — determine the specific error for UI
        try {
          const parsedResults = JSON.parse(profilingJobData.results.data);

          if (parsedResults.error) {
            const errorMessage = parsedResults.error || "Profiling job failed";
            console.error("Profiling job error:", errorMessage);
            setDataProfile(null);
            setProfilingError({
              message: errorMessage,
              timestamp: new Date().toISOString(),
              retryable: true,
            });
            setIsProfiling(false);
          } else if (parsedResults.state === "FAILED") {
            console.error("Profiling task failed");
            setDataProfile(null);
            setProfilingError({
              message: "Profiling task failed. Please try uploading your file again.",
              timestamp: new Date().toISOString(),
              retryable: true,
            });
            setIsProfiling(false);
          }
          // else: no message yet — still waiting for results
        } catch (error) {
          console.error("Failed to parse profiling results:", error);
          setDataProfile(null);
          setProfilingError({
            message: "Failed to process profiling results. Please try again.",
            timestamp: new Date().toISOString(),
            retryable: true,
          });
          setIsProfiling(false);
        }
      }

      if (isComplete) {
        setIsProfiling(false);
      }
    }
  }, [profilingJobData, validationState.summary.errorRows]);

  const { startValidation } = useStartValidation();

  // Auto-start validation when ingestion and sourceFile are available
  useEffect(() => {
    if (ingestion && sourceFile && !hasStartedValidation && !validationJobId && !isValidating) {
      setHasStartedValidation(true);
      setIsValidating(true);

      // Capture the source file being validated (for comparison if user goes back and selects a different file)
      setRecoveredSourceFile(sourceFile);

      startValidation({
        sourceFile: sourceFile,
      }).then((result) => {
        if (result?.validationJobId) {
          setValidationJobId(result.validationJobId);
        } else {
          // Failed to start validation
          setValidationState((prev) => ({
            ...prev,
            hasErrors: true,
            hasWarnings: false,
            isComplete: true,
          }));
          setIsValidating(false);
        }
      });
    }
  }, [ingestion, sourceFile, hasStartedValidation, validationJobId, isValidating, startValidation]);

  const { startProfiling } = useStartProfiling();

  // Auto-start profiling when ingestion and sourceFile are available
  useEffect(() => {
    if (ingestion && sourceFile && !hasStartedProfiling && !isProfiling && !profilingJobId) {
      setHasStartedProfiling(true);
      setIsProfiling(true);

      startProfiling({
        sourceFile: sourceFile,
      }).then((result) => {
        if (result?.profilingJobId) {
          setProfilingJobId(result.profilingJobId);
        } else {
          // Failed to start profiling
          setDataProfile(null);
          setProfilingError({
            message: "Failed to start profiling job. Please try again.",
            timestamp: new Date().toISOString(),
            retryable: true,
          });
          setIsProfiling(false);
        }
      });
    }
  }, [isProfiling, startProfiling, sourceFile, ingestion, profilingJobId, hasStartedProfiling]);

  return {
    validationState,
    dataProfile,
    profilingError,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
    ingestion,
    isLoadingIngestion,
    /** Source file URI recovered from existing jobs (for page refresh scenario) */
    recoveredSourceFile,
  };
};
